import React from 'react'

import { FaInfoCircle, FaTrophy } from 'react-icons/fa'

import ProgressTrend from './ProgressTrend'
import SummaryStat from './SummaryStat'
import { Progress, SegmentedControl, Tooltip } from '../../../_components'
import {
  GamificationCard,
  GamificationContent,
  GamificationHeader,
  GamificationMeta,
  MiniStats,
  ProgressStatus,
  ProgressBody,
  ProgressInsights,
  StatsGrid,
  SummaryHeader,
} from '../styles'

import type {
  GamificationSummary,
  ProgressPeriod,
  ProgressTrendPoint,
} from './types'

const periodOptions: { value: ProgressPeriod; label: string }[] = [
  { value: 'current-month', label: 'Este mes' },
  { value: 'previous-month', label: 'Mes pasado' },
  { value: 'six-months', label: '6 meses' },
  { value: 'year', label: '1 año' },
]

interface ProgressSummaryProps {
  activity: number
  practices: number
  sharedChallenges: number
  gamification: GamificationSummary
  period: ProgressPeriod
  onPeriodChange: (period: ProgressPeriod) => void
  loading?: boolean
  error?: string | null
  hasActivity?: boolean
  trend: ProgressTrendPoint[]
}

const ProgressSummary = ({
  activity,
  gamification,
  error,
  hasActivity = true,
  loading = false,
  onPeriodChange,
  period,
  practices,
  sharedChallenges,
  trend,
}: ProgressSummaryProps) => (
  <MiniStats aria-labelledby="vocal-rhythm-title" aria-busy={loading}>
    <SummaryHeader>
      <div>
        <span>Tu avance</span>
        <h2 id="vocal-rhythm-title">Tu ritmo vocal</h2>
        <p>La constancia también se entrena.</p>
      </div>
      <SegmentedControl
        label="Período del avance vocal"
        options={periodOptions}
        value={period}
        onChange={onPeriodChange}
      />
    </SummaryHeader>

    {loading && (
      <ProgressStatus role="status">
        Estamos actualizando tu progreso vocal.
      </ProgressStatus>
    )}

    {error && <ProgressStatus role="alert">{error}</ProgressStatus>}

    {!loading && !error && !hasActivity && (
      <ProgressStatus>
        Todavía no registramos actividad este mes. Tu aplausómetro se
        actualizará cuando inicies sesión, practiques o participes en desafíos.
      </ProgressStatus>
    )}

    <ProgressBody $compact={period === 'current-month'}>
      <ProgressInsights>
        {period === 'current-month' ? (
          <StatsGrid>
            <SummaryStat
              image="/assets/redesign/progress-cards/practice.png"
              value={practices}
              label="Clases consecutivas"
            />
            <SummaryStat
              image="/assets/redesign/progress-cards/activity.png"
              value={activity}
              label="Entrenamientos en casa"
            />
            <SummaryStat
              image="/assets/redesign/progress-cards/challenge.png"
              value={sharedChallenges}
              label="Desafíos compartidos"
            />
          </StatsGrid>
        ) : (
          <ProgressTrend points={trend} />
        )}
      </ProgressInsights>

      <GamificationCard>
        <GamificationContent>
          <GamificationHeader>
            <span className="gamification-trophy">
              <FaTrophy aria-hidden="true" />
            </span>
            <div className="gamification-title">
              <small>Aplausómetro · este mes</small>
              <strong>{gamification.points} Ah-plausos</strong>
            </div>
            <Tooltip
              label="Cómo se calculan los Ah-plausos"
              content={
                <>
                  <strong>Así se calcula este mes</strong>
                  <ul>
                    <li>{practices} por clases consecutivas.</li>
                    <li>{activity} por entrenamientos en casa.</li>
                    <li>{sharedChallenges} por desafíos compartidos.</li>
                    <li>
                      +{gamification.activityBonus} por superar los accesos del
                      mes anterior.
                    </li>
                    <li>
                      +{gamification.practicesBonus} por superar las prácticas
                      del mes anterior.
                    </li>
                  </ul>
                </>
              }
            >
              <FaInfoCircle aria-hidden="true" />
            </Tooltip>
          </GamificationHeader>
          <Progress
            value={gamification.pointsInLevel}
            max={gamification.pointsPerLevel}
            label={`Progreso hacia el nivel ${gamification.level + 1}`}
            size="medium"
            variant="celebration"
            marker={
              <img
                src="/assets/redesign/gamification/clapping-hands.png"
                alt=""
              />
            }
          />
          <GamificationMeta>
            <span>
              Nivel {gamification.level} · {gamification.pointsInLevel} puntos
            </span>
            <strong>
              {gamification.pointsToNextLevel} para nivel{' '}
              {gamification.level + 1}
            </strong>
          </GamificationMeta>
        </GamificationContent>
      </GamificationCard>
    </ProgressBody>
  </MiniStats>
)

export default ProgressSummary
